/**
 * MobileProfile — Lightweight standalone mobile profile component.
 * Replicates ProfileClient mobile-mode UI without the heavy desktop overhead.
 * Uses shared hooks for business logic, CSS variables for theming.
 *
 * NO: AnimationLayer, useScale, useDynamicBackground, useSwipe, MainNavbar,
 *     PixelatedTurtle, ProfileFrameDesign, headerDesign, localTheme
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Heart, MessageSquare, Share2, ThumbsDown, ChevronDown, TrendingUp, TrendingDown,
  Flame, Calendar, Clock, Repeat2, AlertCircle, Shield,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useProfileData, type ProfileData } from '../shared/hooks/useProfileData';
import { type FeedPost, type Reply, sortPosts, transformBackendPost, normalizeLikeCounts } from '../shared/hooks/useFeedData';
import { getLegacyPostUrl, getCommunityPostUrl, getForumPostUrl } from '../../utils/postUrls';
import { PostBadges } from '../desktop/PostBadges';
import { selectPreviewComments } from '../../utils/commentPreview';
import resolveAvatar from '../../lib/resolveAvatar';
import { MobileProfileThemeCustomizer } from './MobileThemeBottomSheet';
import { MobilePostOverlay } from './MobilePostOverlay';
import { SubPanelSwiper } from './Navigation';
import { profileEventBus } from '../../lib/profileEventBus';
import { transformColor, type ColorTransformOptions } from '../../utils/colorTransform';
import { getBackground, getCardBackground } from '../../utils/backgroundHelpers';
import { getThemeByKey, type PresetThemeKey, PRESET_THEMES } from '@theme/tokens';

import { fetchWithAuth } from '../../lib/fetchWithAuth';
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getPostUrl(post: FeedPost, profileUsername?: string): string {
  if (post.slug && post.communitySlug && post.postType === 'forum')
    return getForumPostUrl(post.communitySlug, post.slug);
  if (post.slug && post.communitySlug && post.postType === 'community')
    return getCommunityPostUrl(post.communitySlug, post.slug);
  if (post.slug && (post.authorUsername || profileUsername))
    return getLegacyPostUrl(post.authorUsername || profileUsername || 'unknown', post.slug);
  return getLegacyPostUrl(post.authorUsername || profileUsername || 'unknown', post.slug || post.id.toString());
}

// ---------------------------------------------------------------------------
// Theme helpers
// ---------------------------------------------------------------------------

function buildProfileThemeVars(raw: any): React.CSSProperties {
  if (!raw) return {};
  let c = raw;
  if (raw.baseTheme && raw.baseTheme in PRESET_THEMES) {
    const preset = getThemeByKey(raw.baseTheme as PresetThemeKey);
    c = { ...preset, ...raw };
    // Preserve preset card gradient (saved data may have stale values)
    if (preset.cardGradientEnabled) {
      c.cardGradientEnabled = true;
      if (!c.cardGradientColor1?.startsWith('#')) c.cardGradientColor1 = preset.cardGradientColor1;
      if (!c.cardGradientColor2?.startsWith('#')) c.cardGradientColor2 = preset.cardGradientColor2;
    }
  }
  if (!c.primaryColor) return {};

  const colorSpace = c.colorSpace || 'Off';

  const hexToRgbaWithOpacity = (hex: string, opacity: number) => {
    if (!hex || !hex.startsWith('#') || hex.length < 7) return hex;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
  };

  let textColor = c.textColor;
  let accentColor = c.accentColor;
  let primaryColor = c.primaryColor;
  let secondaryColor = c.secondaryColor;
  let cardBg = c.cardBg;

  if (colorSpace !== 'Off') {
    const opts: ColorTransformOptions = {
      colorSpace,
      temperatureLimit: c.temperatureLimit ?? 50,
      magentaLimit: c.magentaLimit ?? 50,
      intensityLimit: c.intensityLimit ?? 50,
      saturationLimit: c.backgroundSaturationLimit ?? 50,
      brightnessLimit: c.backgroundBrightnessLimit ?? 50,
      redLimit: c.redLimit ?? 50,
      greenLimit: c.greenLimit ?? 50,
      blueLimit: c.blueLimit ?? 50,
      contrast: c.backgroundContrast ?? 50,
      hueGravity: c.backgroundHueGravity ?? 0,
      graySaturation: c.backgroundGraySaturation ?? 0,
      targetHue: c.backgroundDefaultHue ?? 184,
    };
    textColor = transformColor(textColor, opts);
    accentColor = transformColor(accentColor, opts);
    primaryColor = transformColor(primaryColor, opts);
    secondaryColor = transformColor(secondaryColor, opts);
    if (cardBg?.startsWith('#')) cardBg = transformColor(cardBg, opts);
  }

  const navbarBg = hexToRgbaWithOpacity(
    c.navbarBg?.startsWith('#') ? (colorSpace !== 'Off' ? transformColor(c.navbarBg, { colorSpace } as any) : c.navbarBg) : primaryColor,
    c.navbarOpacity ?? 92,
  );
  const tabBarBg = c.tabBarBg
    ? hexToRgbaWithOpacity(c.tabBarBg.startsWith('#') && colorSpace !== 'Off' ? transformColor(c.tabBarBg, { colorSpace } as any) : c.tabBarBg, c.tabBarOpacity ?? 88)
    : navbarBg;

  const bg = getBackground(
    { primaryColor, secondaryColor, cardBg: cardBg || primaryColor },
    { gradientEnabled: c.gradientEnabled, gradientAngle: c.gradientAngle, gradientFavorability: c.gradientFavorability, gradientStrength: c.gradientStrength },
    primaryColor,
  );

  return {
    '--text-color': textColor,
    '--color-accent': accentColor,
    '--primary-color': primaryColor,
    '--secondary-color': secondaryColor,
    '--card-bg': getCardBackground(
      { primaryColor, secondaryColor, cardBg: cardBg || primaryColor, cardGradientColor1: c.cardGradientColor1, cardGradientColor2: c.cardGradientColor2 },
      { cardGradientEnabled: c.cardGradientEnabled, cardGradientAngle: c.cardGradientAngle, cardGradientFavorability: c.cardGradientFavorability, cardGradientStrength: c.cardGradientStrength, cardOpacity: c.cardOpacity },
      primaryColor,
    ),
    '--navbar-bg': navbarBg,
    '--tab-bar-bg': tabBarBg,
    '--app-bg': bg,
  } as React.CSSProperties;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TABS = [
  { id: 'personal' as const, label: 'Personal' },
  { id: 'media' as const, label: 'Media' },
  { id: 'community' as const, label: 'Community' },
  { id: 'replies' as const, label: 'Replies' },
];

const SORT_OPTIONS = [
  { value: 'newest' as const, label: 'Newest', icon: Calendar },
  { value: 'oldest' as const, label: 'Oldest', icon: Clock },
  { value: 'popular' as const, label: 'Popular', icon: TrendingUp },
  { value: 'unpopular' as const, label: 'Unpopular', icon: TrendingDown },
  { value: 'controversial' as const, label: 'Controversial', icon: Flame },
];

type ProfileTab = 'personal' | 'media' | 'community' | 'replies';
type SortValue = typeof SORT_OPTIONS[number]['value'];

// ---------------------------------------------------------------------------
// MobileProfile
// ---------------------------------------------------------------------------

export interface MobileProfileProps {
  activePanel: number;
  onPanelChange: (index: number) => void;
  screenWidth: number;
  /** If provided, show this user's profile. Otherwise show auth user's profile. */
  userId?: string;
}

export function MobileProfile({ activePanel, onPanelChange, screenWidth, userId: userIdProp }: MobileProfileProps) {
  const { user: authUser, refreshUser } = useAuth();
  const resolvedUserId = userIdProp || authUser?.id?.toString();

  const {
    profile, posts, isLoading, isFollowing, isMutualFollowers,
    followUser, unfollowUser, likePost, dislikePost, refreshProfile,
  } = useProfileData(resolvedUserId);

  // Local state
  const [activeTab, setActiveTab] = useState<ProfileTab>('personal');
  const [sortBy, setSortBy] = useState<SortValue>('newest');
  const [communityPosts, setCommunityPosts] = useState<any[]>([]);
  const [replies, setReplies] = useState<any[]>([]);
  const [showPostModal, setShowPostModal] = useState(false);
  const [isShareAnimating, setIsShareAnimating] = useState(false);

  const currentSortOption = SORT_OPTIONS.find(o => o.value === sortBy) || SORT_OPTIONS[0];

  // Compute profile theme CSS vars from the user's own profile customization
  const profileThemeVars = useMemo(
    () => buildProfileThemeVars(profile?.profileCustomization),
    [profile?.profileCustomization]
  );

  // Fix #3: Dispatch profile theme vars to MobileShell for header theming
  useEffect(() => {
    if (Object.keys(profileThemeVars).length > 0) {
      window.dispatchEvent(new CustomEvent('mobileProfileThemeReady', { detail: profileThemeVars }));
    }
  }, [profileThemeVars]);

  // Fetch community posts
  useEffect(() => {
    if (!resolvedUserId) return;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/profile/${resolvedUserId}/community-posts`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setCommunityPosts((data.posts || []).map(normalizeLikeCounts));
        }
      } catch { /* silent */ }
    })();
  }, [resolvedUserId]);

  // Fetch replies
  useEffect(() => {
    if (!resolvedUserId) return;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/profile/${resolvedUserId}/replies`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setReplies((data.replies || []).map(normalizeLikeCounts));
        }
      } catch { /* silent */ }
    })();
  }, [resolvedUserId]);

  // Listen for profile updates (e.g. after editing profile)
  useEffect(() => {
    const unsubscribe = profileEventBus.on('profile:updated', async (data) => {
      if (authUser && data.userId === authUser.id.toString()) {
        await refreshUser();
        refreshProfile();
      }
    });
    return () => { unsubscribe(); };
  }, [refreshUser, authUser, refreshProfile]);

  // Listen for theme customizer saves — refresh profile data to recompute theme vars
  useEffect(() => {
    const handleThemeUpdate = () => {
      refreshProfile();
    };
    window.addEventListener('profileThemeUpdated', handleThemeUpdate);
    return () => window.removeEventListener('profileThemeUpdated', handleThemeUpdate);
  }, [refreshProfile]);

  // Wall post creation
  const handleCreatePost = useCallback(async (postData: {
    title: string; content: string; tags: string[]; mediaUrl: string; mediaPosition: 'above' | 'below';
  }) => {
    if (!profile) return;
    try {
      const response = await fetchWithAuth('/api/posts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: postData.title.trim() || undefined,
          content: postData.content,
          tags: postData.tags,
          mediaUrl: postData.mediaUrl || undefined,
          aboveContent: postData.mediaPosition === 'above',
          belowContent: postData.mediaPosition === 'below',
        }),
      });
      if (response.ok) {
        refreshProfile();
        setShowPostModal(false);
      }
    } catch { /* silent */ }
  }, [profile, refreshProfile]);

  // Comment like/dislike (optimistic)
  const likeComment = useCallback(async (commentId: string, postId: string) => {
    try {
      const res = await fetchWithAuth(`/api/comments/${commentId}/like`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) refreshProfile();
    } catch { /* silent */ }
  }, [refreshProfile]);

  const dislikeComment = useCallback(async (commentId: string, postId: string) => {
    try {
      const res = await fetchWithAuth(`/api/comments/${commentId}/dislike`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) refreshProfile();
    } catch { /* silent */ }
  }, [refreshProfile]);

  // Share button handler
  const handleShareClick = useCallback(() => {
    setIsShareAnimating(true);
    setTimeout(() => {
      setShowPostModal(true);
      setIsShareAnimating(false);
    }, 300);
  }, []);

  // Build current user object for PostCreationModal
  const currentUser = authUser ? {
    id: authUser.id.toString(),
    username: authUser.username,
    displayName: authUser.name || authUser.username,
    avatar: authUser.profilePicture || undefined,
    isSiteAdmin: authUser.isSiteAdmin || false,
    isSuperAdmin: authUser.isSuperAdmin || false,
    isSiteModerator: authUser.isSiteModerator || false,
  } : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div
          className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
          style={{ borderColor: 'var(--color-accent)', borderTopColor: 'transparent' }}
        />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center h-full">
        <span className="text-sm" style={{ color: 'var(--text-color)', opacity: 0.4 }}>
          Log in to see your profile
        </span>
      </div>
    );
  }

  return (
    <div className="h-full" style={{ ...profileThemeVars, background: profileThemeVars['--app-bg' as keyof typeof profileThemeVars] || undefined }}>
      <SubPanelSwiper activePanel={activePanel} onPanelChange={onPanelChange} screenWidth={screenWidth}>
        {[
          <LeftPanel
            key="left"
            profile={profile}
            currentUser={currentUser}
            isShareAnimating={isShareAnimating}
            onShareClick={handleShareClick}
          />,
          <CenterPanel
            key="center"
            profile={profile}
            posts={posts}
            communityPosts={communityPosts}
            replies={replies}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            sortBy={sortBy}
            setSortBy={setSortBy}
            currentSortOption={currentSortOption}
            isFollowing={isFollowing}
            isMutualFollowers={isMutualFollowers}
            onFollow={followUser}
            onUnfollow={unfollowUser}
            onLike={likePost}
            onDislike={dislikePost}
            onLikeComment={likeComment}
            onDislikeComment={dislikeComment}
          />,
          <RightPanel
            key="right"
            profile={profile}
            currentUser={currentUser}
          />,
        ]}
      </SubPanelSwiper>

      {/* Post creation overlay — rendered outside SubPanelSwiper via portal */}
      <MobilePostOverlay
        isOpen={showPostModal}
        onClose={() => setShowPostModal(false)}
        onSubmit={handleCreatePost}
        currentUser={currentUser}
        heading="Post to Wall"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Left Panel — Share + Post Creation
// ---------------------------------------------------------------------------

function LeftPanel({
  profile, currentUser,
  isShareAnimating, onShareClick,
}: {
  profile: ProfileData;
  currentUser: any;
  isShareAnimating: boolean;
  onShareClick: () => void;
}) {
  return (
    <div className="pt-6 px-4 h-full overflow-auto flex flex-col items-stretch gap-3" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top) + 1rem)' }}>
      {currentUser && (
        <>
          <button
            onClick={onShareClick}
            className="rounded-lg shadow-lg font-semibold overflow-hidden w-full"
            style={{
              backgroundColor: 'var(--color-accent)',
              color: 'var(--text-color)',
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              minHeight: '48px',
            }}
          >
            <span style={{
              display: 'inline-block',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              opacity: isShareAnimating ? 0 : 1,
              transform: isShareAnimating ? 'translateX(-20px)' : 'translateX(0)',
            }}>
              Share
            </span>
          </button>
          <p className="text-xs text-center" style={{ color: 'var(--text-color)', opacity: 0.4 }}>
            Tap to create a post
          </p>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Center Panel — Profile Card + Tabs + Posts
// ---------------------------------------------------------------------------

function CenterPanel({
  profile, posts, communityPosts, replies,
  activeTab, setActiveTab, sortBy, setSortBy, currentSortOption,
  isFollowing, isMutualFollowers, onFollow, onUnfollow,
  onLike, onDislike, onLikeComment, onDislikeComment,
}: {
  profile: ProfileData;
  posts: FeedPost[];
  communityPosts: any[];
  replies: any[];
  activeTab: ProfileTab;
  setActiveTab: (tab: ProfileTab) => void;
  sortBy: SortValue;
  setSortBy: (s: SortValue) => void;
  currentSortOption: typeof SORT_OPTIONS[number];
  isFollowing: boolean;
  isMutualFollowers: boolean;
  onFollow: () => void;
  onUnfollow: () => void;
  onLike: (id: string) => void;
  onDislike: (id: string) => void;
  onLikeComment: (commentId: string, postId: string) => void;
  onDislikeComment: (commentId: string, postId: string) => void;
}) {
  const navigate = useNavigate();
  const [showSortMenu, setShowSortMenu] = useState(false);
  const sortedPosts = sortPosts(posts, sortBy);
  const sortedCommunityPosts = [...communityPosts].sort((a, b) => {
    switch (sortBy) {
      case 'newest': return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      case 'oldest': return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      case 'popular': return (b.likes || 0) - (a.likes || 0);
      case 'unpopular': return (b.dislikes || 0) - (a.dislikes || 0);
      case 'controversial': return Math.min(b.likes || 0, b.dislikes || 0) - Math.min(a.likes || 0, a.dislikes || 0);
      default: return 0;
    }
  });
  const sortedReplies = [...replies].sort((a, b) => {
    switch (sortBy) {
      case 'newest': return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      case 'oldest': return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      default: return 0;
    }
  });
  const scrollRef = useRef<HTMLDivElement>(null);
  const tabBarRef = useRef<HTMLDivElement>(null);

  // Scroll tab bar into view when switching tabs
  const handleTabSwitch = useCallback((tab: ProfileTab) => {
    setActiveTab(tab);
    // Scroll so the tab bar is at the top
    tabBarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [setActiveTab]);

  return (
    <div ref={scrollRef} className="flex flex-col items-center w-full px-2 h-full overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
      {/* ── Profile Card ── */}
      <div className="rounded-2xl w-full" style={{
        background: 'var(--card-bg)',
        padding: '16px',
        marginBottom: '24px',
      }}>
        <div className="flex flex-col items-start gap-4">
          {/* Avatar */}
          <div
            className="relative overflow-hidden"
            style={{
              width: 96,
              height: 96,
              border: '4px solid var(--color-accent)',
              borderRadius: '50%',
            }}
          >
            {profile.avatar ? (
              <img src={profile.avatar} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-2xl font-bold" style={{ backgroundColor: 'var(--card-bg)', color: 'var(--color-accent)' }}>
                {profile.displayName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 w-full">
            {/* Name + Follow */}
            <div className="flex items-center mb-2 gap-3">
              <h1 className="font-bold text-2xl" style={{ color: 'var(--text-color)' }}>
                {profile.displayName}
              </h1>
              {!profile.isCurrentUser && (
                <button
                  onClick={() => isFollowing ? onUnfollow() : onFollow()}
                  className="rounded-lg font-semibold"
                  style={{
                    padding: '8px 20px',
                    fontSize: '15px',
                    minWidth: '112px',
                    backgroundColor: isFollowing ? 'rgba(255, 255, 255, 0.1)' : 'var(--color-accent)',
                    color: 'var(--text-color)',
                    border: isFollowing ? '1px solid var(--color-accent)' : '1px solid transparent',
                  }}
                >
                  {isMutualFollowers ? 'Friends' : isFollowing ? 'Following' : 'Follow'}
                </button>
              )}
            </div>

            {/* Title */}
            <p className="opacity-90 text-lg mb-3" style={{ color: 'var(--text-color)' }}>
              {profile.title}
            </p>

            {/* Bio */}
            {profile.bio && (
              <div className="flex flex-col gap-2">
                {profile.bio.split('\n').filter((line: string) => line.trim()).map((line: string, i: number) => (
                  <p key={i} className="italic opacity-85 text-base" style={{ color: 'var(--text-color)' }}>
                    {line}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Tab Bar (sticky so it stays visible when scrolling) ── */}
      <div ref={tabBarRef} className="flex items-center w-full sticky top-0" style={{
        paddingTop: '8px',
        paddingBottom: '8px',
        position: 'sticky',
        top: 0,
        zIndex: 30,
        background: 'transparent',
      }}>
        {/* Scrollable tabs */}
        <div className="flex items-center overflow-x-auto flex-1 gap-1.5" style={{ WebkitOverflowScrolling: 'touch' }}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleTabSwitch(tab.id); }}
              className="rounded-xl font-semibold whitespace-nowrap text-center"
              style={{
                backgroundColor: activeTab === tab.id ? 'var(--color-accent)' : 'var(--tab-bar-bg, rgba(30, 58, 95, 0.95))',
                color: 'var(--text-color)',
                border: `2px solid ${activeTab === tab.id ? 'var(--color-accent)' : 'transparent'}`,
                padding: '6px 14px',
                fontSize: '12px',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Sort dropdown - outside overflow container so it's not clipped */}
        <div className="relative shrink-0 ml-1" style={{ zIndex: 41 }}>
          <button
            onClick={() => setShowSortMenu(prev => !prev)}
            className="flex items-center rounded-lg font-medium"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-color)',
              gap: '4px',
              padding: '4px 8px',
              fontSize: '11px',
            }}
          >
            <currentSortOption.icon size={14} />
            <ChevronDown size={14} className={showSortMenu ? 'rotate-180' : ''} style={{ transition: 'transform 0.2s' }} />
          </button>
          {showSortMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowSortMenu(false)} />
              <div className="absolute top-full right-0 rounded-lg overflow-hidden shadow-2xl z-50" style={{
                background: 'var(--card-bg)',
                border: '1px solid rgba(var(--accent-rgb, 6,182,212), 0.25)',
                marginTop: '4px',
                minWidth: '140px',
              }}>
                {SORT_OPTIONS.map(option => (
                  <button
                    key={option.value}
                    onClick={() => { setSortBy(option.value); setShowSortMenu(false); }}
                    className="flex items-center gap-2 w-full text-left"
                    style={{
                      padding: '10px 14px',
                      backgroundColor: sortBy === option.value ? 'rgba(var(--accent-rgb, 6,182,212), 0.2)' : 'transparent',
                      color: 'var(--text-color)',
                      fontSize: '13px',
                    }}
                  >
                    <option.icon size={14} style={{ opacity: 0.7 }} />
                    {option.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Wavy divider ── */}
      <div className="w-full" style={{ marginTop: '4px', marginBottom: '16px' }}>
        <svg width="100%" height="12" viewBox="0 0 1000 20" preserveAspectRatio="none" style={{ display: 'block' }}>
          <path
            d="M0,10 Q25,0 50,10 T100,10 T150,10 T200,10 T250,10 T300,10 T350,10 T400,10 T450,10 T500,10 T550,10 T600,10 T650,10 T700,10 T750,10 T800,10 T850,10 T900,10 T950,10 T1000,10"
            fill="none"
            stroke="var(--color-accent)"
            strokeWidth="2"
            opacity="0.4"
          />
        </svg>
      </div>

      {/* ── Personal Tab ── */}
      {activeTab === 'personal' && (
        <div className="w-full flex flex-col gap-4">
          {sortedPosts.length === 0 ? (
            <div className="rounded-xl p-6" style={{ background: 'var(--card-bg)' }}>
              <p className="text-center opacity-60" style={{ color: 'var(--text-color)' }}>No posts yet</p>
            </div>
          ) : (
            sortedPosts.map(post => (
              <ProfilePostCard
                key={post.id}
                post={post}
                profileUsername={profile.username}
                profileAvatar={profile.avatar}
                profileDisplayName={profile.displayName}
                profileCustomization={profile.profileCustomization}
                onLike={onLike}
                onDislike={onDislike}
                onLikeComment={onLikeComment}
                onDislikeComment={onDislikeComment}
                navigate={navigate}
              />
            ))
          )}
        </div>
      )}

      {/* ── Media Tab ── */}
      {activeTab === 'media' && (
        <div className="w-full flex flex-col gap-4">
          {(() => {
            const mediaPosts = sortedPosts.filter(p => p.mediaUrl || p.imageUrl);
            if (mediaPosts.length === 0) {
              return (
                <div className="rounded-xl p-6" style={{ background: 'var(--card-bg)' }}>
                  <p className="text-center opacity-60" style={{ color: 'var(--text-color)' }}>No media posts yet</p>
                </div>
              );
            }
            return mediaPosts.map(post => (
              <div key={post.id} className="rounded-xl relative" style={{ background: 'var(--card-bg)', padding: '1rem' }}>
                {post.mediaUrl && (
                  <div className="mb-3 rounded-lg overflow-hidden">
                    <img src={post.mediaUrl} alt="" className="w-full h-auto" style={{ maxHeight: '300px', objectFit: 'cover' }} loading="lazy" />
                  </div>
                )}
                <Link to={getPostUrl(post, profile.username)} className="block">
                  <p className="leading-relaxed opacity-90 text-sm" style={{ color: 'var(--text-color)' }}>{post.content}</p>
                </Link>
                <div className="flex items-center gap-4 pt-3 mt-3" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <button onClick={() => onLike(post.id)} className="flex items-center gap-1" style={{ color: post.liked ? '#ef4444' : 'var(--text-color)', opacity: post.liked ? 1 : 0.6 }}>
                    <Heart size={16} fill={post.liked ? '#ef4444' : 'none'} />
                    <span className="text-xs">{post.likes}</span>
                  </button>
                  <button onClick={() => navigate(getPostUrl(post, profile.username))} className="flex items-center gap-1" style={{ color: 'var(--text-color)', opacity: 0.6 }}>
                    <MessageSquare size={16} />
                    <span className="text-xs">{post.comments}</span>
                  </button>
                  <button className="flex items-center gap-1" style={{ color: 'var(--text-color)', opacity: 0.6 }}>
                    <Share2 size={16} />
                  </button>
                </div>
              </div>
            ));
          })()}
        </div>
      )}

      {/* ── Community Tab ── */}
      {activeTab === 'community' && (
        <div className="w-full flex flex-col gap-4">
          {sortedCommunityPosts.length === 0 ? (
            <div className="rounded-xl p-6" style={{ background: 'var(--card-bg)' }}>
              <p className="text-center opacity-60" style={{ color: 'var(--text-color)' }}>No community posts yet</p>
            </div>
          ) : (
            sortedCommunityPosts.map((post: any) => (
              <div key={post.id} className="rounded-xl relative" style={{ background: 'var(--card-bg)', padding: '1rem' }}>
                <Link
                  to={post.slug && post.communitySlug
                    ? (post.postType === 'forum' ? getForumPostUrl(post.communitySlug, post.slug) : getCommunityPostUrl(post.communitySlug, post.slug))
                    : `/posts/${post.id}`}
                  className="block"
                >
                  <h2 className="text-lg font-bold" style={{ color: 'var(--text-color)' }}>{post.title || 'Community Post'}</h2>
                  <p className="text-xs opacity-60 mt-1 mb-2" style={{ color: 'var(--text-color)' }}>in {post.communityName}</p>
                  <p className="leading-relaxed opacity-90 text-sm" style={{ color: 'var(--text-color)' }}>{post.content}</p>
                </Link>
                <div className="flex items-center gap-4 pt-3 mt-3" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-color)', opacity: 0.6 }}>
                    <Heart size={16} /> {post.likes}
                  </span>
                  <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-color)', opacity: 0.6 }}>
                    <MessageSquare size={16} /> {post.comments}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ── Replies Tab ── */}
      {activeTab === 'replies' && (
        <div className="w-full flex flex-col gap-4">
          {sortedReplies.length === 0 ? (
            <div className="rounded-xl p-6" style={{ background: 'var(--card-bg)' }}>
              <p className="text-center opacity-60" style={{ color: 'var(--text-color)' }}>No replies yet</p>
            </div>
          ) : (
            sortedReplies.map((reply: any) => {
              const postUrl = reply.type === 'legacy'
                ? getLegacyPostUrl(reply.post.author.username, reply.post.slug || String(reply.post.id))
                : getCommunityPostUrl(reply.post.communitySlug, reply.post.slug || String(reply.post.id));
              return (
                <div key={reply.id} className="rounded-xl p-4" style={{ background: 'var(--card-bg)' }}>
                  <p className="text-xs opacity-70 mb-2" style={{ color: 'var(--text-color)' }}>
                    replied to @{reply.post.author.username}
                    {reply.post.title && <span> — {reply.post.title}</span>}
                  </p>
                  <p className="text-sm mb-3" style={{ color: 'var(--text-color)' }}>{reply.content}</p>
                  <div
                    className="rounded-lg p-3 border-l-4 cursor-pointer"
                    style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'var(--color-accent)' }}
                    onClick={() => navigate(postUrl)}
                  >
                    <p className="text-xs opacity-80 line-clamp-2" style={{ color: 'var(--text-color)' }}>{reply.post.content}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Bottom padding */}
      <div className="h-24 shrink-0" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Right Panel — Theme Customizer + Moderation
// ---------------------------------------------------------------------------

function RightPanel({ profile, currentUser }: { profile: ProfileData; currentUser: any }) {
  const [showThemeCustomizer, setShowThemeCustomizer] = useState(false);

  return (
    <div className="pt-6 px-4 h-full overflow-auto flex flex-col gap-3">
      {/* Theme Customizer Button */}
      {profile.isCurrentUser && (
        <button
          onClick={() => setShowThemeCustomizer(true)}
          className="rounded-lg font-semibold w-full flex items-center justify-center gap-2"
          style={{
            backgroundColor: 'var(--color-accent)',
            color: 'var(--text-color)',
            padding: '0.75rem 1rem',
            fontSize: '0.875rem',
            minHeight: '48px',
          }}
        >
          Customize Theme
        </button>
      )}

      {/* Moderation button (for admins/mods viewing other users) */}
      {currentUser && !profile.isCurrentUser && (
        currentUser.isSiteModerator || currentUser.isSiteAdmin || currentUser.isSuperAdmin
      ) && (
        <button
          className="rounded-lg shadow-lg font-semibold overflow-hidden flex items-center gap-2 w-full justify-center"
          style={{
            backgroundColor: 'var(--color-accent)',
            color: 'var(--text-color)',
            padding: '0.75rem 1rem',
            fontSize: '0.875rem',
            minHeight: '48px',
          }}
        >
          <Shield size={20} />
          <span>Moderate</span>
        </button>
      )}

      {/* Theme Customizer Modal */}
      {showThemeCustomizer && profile.isCurrentUser && (
        <MobileProfileThemeCustomizer
          isOpen={showThemeCustomizer}
          onClose={() => setShowThemeCustomizer(false)}
          userId={profile.id}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Profile Post Card — for Personal tab (profile-specific rendering)
// ---------------------------------------------------------------------------

function ProfilePostCard({
  post, profileUsername, profileAvatar, profileDisplayName, profileCustomization, onLike, onDislike, onLikeComment, onDislikeComment, navigate,
}: {
  post: FeedPost;
  profileUsername: string;
  profileAvatar?: string;
  profileDisplayName?: string;
  profileCustomization?: any;
  onLike: (id: string) => void;
  onDislike: (id: string) => void;
  onLikeComment: (commentId: string, postId: string) => void;
  onDislikeComment: (commentId: string, postId: string) => void;
  navigate: (path: string, options?: any) => void;
}) {
  const postUrl = getPostUrl(post, profileUsername);

  // Always pass profile theme context so post overlay knows to fetch and use profile theme
  const navState = {
    state: {
      themeContext: { type: 'profile' as const },
    },
  };

  return (
    <div className="rounded-xl relative" style={{ background: 'var(--card-bg)', padding: '1rem' }}>
      <PostBadges post={post as any} textColor="var(--text-color)" accentColor="var(--color-accent)" />

      {/* Author row */}
      <div className="flex items-center justify-between mb-2">
        <Link to={`/u/${profileUsername}`} className="flex items-center gap-2">
          <div
            className="w-7 h-7 rounded-full overflow-hidden shrink-0 flex items-center justify-center"
            style={{ background: 'var(--color-accent)' }}
          >
            {profileAvatar ? (
              <img src={resolveAvatar(profileAvatar)} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-xs font-bold" style={{ color: 'var(--app-bg, #0a0a0a)' }}>
                {(profileDisplayName || profileUsername).charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <span className="text-xs font-semibold" style={{ color: 'var(--text-color)', opacity: 0.8 }}>
            @{profileUsername}
          </span>
        </Link>
        <div className="text-xs opacity-60" style={{ color: 'var(--text-color)' }}>
          {new Date(post.createdAt).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
        </div>
      </div>

      {/* Title */}
      {post.title && (
        <Link to={postUrl} className="block" {...navState}>
          <h2 className="text-lg font-bold mb-2" style={{ color: 'var(--text-color)' }}>
            {post.title}
          </h2>
        </Link>
      )}

      {/* Media above content */}
      {post.aboveContent && post.mediaUrl && (
        <div className="mb-3 rounded-lg overflow-hidden">
          <img src={post.mediaUrl} alt="" className="w-full h-auto" style={{ maxHeight: '300px', objectFit: 'cover' }} loading="lazy" />
        </div>
      )}

      {/* Content */}
      <Link to={postUrl} className="block" {...navState}>
        <p className="leading-relaxed opacity-90 text-sm" style={{ color: 'var(--text-color)' }}>
          {post.content}
        </p>
      </Link>

      {/* Media below content */}
      {post.belowContent && post.mediaUrl && (
        <div className="mt-3 rounded-lg overflow-hidden">
          <img src={post.mediaUrl} alt="" className="w-full h-auto" style={{ maxHeight: '300px', objectFit: 'cover' }} loading="lazy" />
        </div>
      )}

      {/* Action bar */}
      <div className="flex items-center gap-4 pt-3 mt-3" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <button onClick={() => onLike(post.id)} className="flex items-center gap-1" style={{ color: post.liked ? '#ef4444' : 'var(--text-color)', opacity: post.liked ? 1 : 0.6, minHeight: '44px' }}>
          <Heart size={16} fill={post.liked ? '#ef4444' : 'none'} />
          <span className="text-xs">{post.likes}</span>
        </button>
        <button onClick={() => onDislike(post.id)} className="flex items-center gap-1" style={{ color: post.disliked ? '#f97316' : 'var(--text-color)', opacity: post.disliked ? 1 : 0.6, minHeight: '44px' }}>
          <ThumbsDown size={16} fill={post.disliked ? '#f97316' : 'none'} />
          <span className="text-xs">{post.dislikes}</span>
        </button>
        <button onClick={() => navigate(postUrl, navState)} className="flex items-center gap-1" style={{ color: 'var(--text-color)', opacity: 0.6, minHeight: '44px' }}>
          <MessageSquare size={16} />
          <span className="text-xs">{post.comments}</span>
        </button>
        <button className="flex items-center gap-1" style={{ color: 'var(--text-color)', opacity: 0.6, minHeight: '44px' }}>
          <Repeat2 size={16} />
        </button>
        <button className="flex items-center gap-1" style={{ color: 'var(--text-color)', opacity: 0.6, minHeight: '44px' }}>
          <Share2 size={16} />
        </button>
      </div>

      {/* Comment Previews */}
      {post.replies && post.replies.length > 0 && (() => {
        const previewComments = selectPreviewComments(post.replies as any);
        return (
          <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(var(--accent-rgb, 6,182,212), 0.12)' }}>
            <div className="flex flex-col gap-2.5">
              {previewComments.map((reply: any) => (
                <div key={reply.id} className="flex gap-2">
                  <Link
                    to={`/user/${reply.author.username}`}
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'var(--color-accent)' }}
                  >
                    {reply.author.profilePicture ? (
                      <img src={resolveAvatar(reply.author.profilePicture)} alt="" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <span className="text-xs font-bold" style={{ color: 'var(--app-bg)' }}>
                        {(reply.author.profileName || reply.author.username).charAt(0).toUpperCase()}
                      </span>
                    )}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-xs font-semibold truncate" style={{ color: 'var(--text-color)' }}>
                        {reply.author.profileName || reply.author.username}
                      </span>
                      <span className="text-xs shrink-0" style={{ color: 'var(--text-color)', opacity: 0.4 }}>
                        {new Date(reply.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                    <p className="text-xs line-clamp-2" style={{ color: 'var(--text-color)', opacity: 0.8 }}>{reply.content}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); onLikeComment(reply.id, post.id); }}
                        className="flex items-center gap-1"
                        style={{ color: reply.liked ? '#ef4444' : 'var(--text-color)', opacity: reply.liked ? 1 : 0.4 }}
                      >
                        <Heart size={11} fill={reply.liked ? '#ef4444' : 'none'} />
                        <span className="text-xs">{reply.likes}</span>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); onDislikeComment(reply.id, post.id); }}
                        className="flex items-center gap-1"
                        style={{ color: reply.disliked ? '#f97316' : 'var(--text-color)', opacity: reply.disliked ? 1 : 0.4 }}
                      >
                        <ThumbsDown size={11} fill={reply.disliked ? '#f97316' : 'none'} />
                        <span className="text-xs">{reply.dislikes}</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {post.comments > 3 && (
              <button
                onClick={() => navigate(postUrl, navState)}
                className="text-xs font-semibold mt-2 block"
                style={{ color: 'var(--color-accent)' }}
              >
                View all {post.comments} comments
              </button>
            )}
          </div>
        );
      })()}
    </div>
  );
}
